// HTTP-клиент: JWT с автоматическим обновлением, единый формат ошибок, идемпотентные запросы.

export class ApiError extends Error {
  status: number;
  code: string;
  details: Record<string, any>;
  constructor(status: number, code: string, message: string, details: Record<string, any> = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type Tokens = { access_token: string; refresh_token: string };

export class Api {
  readonly base: string;
  private key: string;
  private refreshing: Promise<boolean> | null = null;
  onUnauthorized: () => void = () => {};

  constructor(app: string, base = "/api/v1") {
    this.base = base;
    this.key = `rbs.${app}.tokens`;
  }

  get tokens(): Tokens | null {
    try {
      const raw = localStorage.getItem(this.key);
      return raw ? (JSON.parse(raw) as Tokens) : null;
    } catch {
      return null;
    }
  }

  set tokens(t: Tokens | null) {
    try {
      if (t) localStorage.setItem(this.key, JSON.stringify(t));
      else localStorage.removeItem(this.key);
    } catch {
      /* приватный режим браузера — токен живёт до перезагрузки */
    }
    this.memory = t;
  }

  private memory: Tokens | null = null;

  get token(): string | null {
    return this.tokens?.access_token ?? this.memory?.access_token ?? null;
  }

  async login(login: string, password: string): Promise<void> {
    this.tokens = await this.raw<Tokens>("POST", "/auth/login", { login, password }, false);
  }

  async loginPin(login: string, pin: string): Promise<void> {
    this.tokens = await this.raw<Tokens>("POST", "/auth/pin", { login, pin }, false);
  }

  /** Вход без пароля, например по одноразовой ссылке-приглашению: сервер отдаёт токены. */
  async loginWith(path: string, body: unknown): Promise<void> {
    this.tokens = await this.raw<Tokens>("POST", path, body, false);
  }

  logout(): void {
    this.tokens = null;
  }

  private async refresh(): Promise<boolean> {
    const rt = this.tokens?.refresh_token;
    if (!rt) return false;
    if (!this.refreshing) {
      this.refreshing = this.raw<Tokens>("POST", "/auth/refresh", { refresh_token: rt }, false)
        .then((t) => {
          this.tokens = t;
          return true;
        })
        .catch(() => false)
        .finally(() => {
          this.refreshing = null;
        });
    }
    return this.refreshing;
  }

  async raw<T>(method: string, path: string, body?: unknown, auth = true, headers: Record<string, string> = {},
               retry = true): Promise<T> {
    const h: Record<string, string> = { ...headers };
    let payload: BodyInit | undefined;
    if (body instanceof FormData) payload = body;
    else if (body !== undefined) {
      h["Content-Type"] = "application/json";
      payload = JSON.stringify(body);
    }
    if (auth && this.token) h["Authorization"] = `Bearer ${this.token}`;
    let res: Response;
    try {
      res = await fetch(this.base + path, { method, headers: h, body: payload });
    } catch {
      throw new ApiError(0, "network", "Нет связи с сервером");
    }
    if (res.status === 401 && auth && retry && (await this.refresh())) {
      return this.raw<T>(method, path, body, auth, headers, false);
    }
    if (res.status === 401 && auth) {
      this.onUnauthorized();
    }
    if (res.status === 204) return undefined as T;
    const ctype = res.headers.get("content-type") || "";
    const data = ctype.includes("json") ? await res.json() : await res.text();
    if (!res.ok) {
      if (typeof data === "object" && data) {
        if (data.error) throw new ApiError(res.status, data.error, data.message, data.details || {});
        if (Array.isArray(data.detail)) {
          const msg = data.detail.map((d: any) => `${(d.loc || []).slice(1).join(".")}: ${d.msg}`).join("; ");
          throw new ApiError(res.status, "validation_failed", msg || "Данные не прошли проверку", { errors: data.detail });
        }
      }
      throw new ApiError(res.status, "http", typeof data === "string" && data ? data : `Ошибка ${res.status}`);
    }
    return data as T;
  }

  get<T = any>(path: string, params?: Record<string, any>): Promise<T> {
    return this.raw<T>("GET", path + qs(params));
  }
  post<T = any>(path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.raw<T>("POST", path, body ?? {}, true, headers);
  }
  patch<T = any>(path: string, body: unknown): Promise<T> {
    return this.raw<T>("PATCH", path, body);
  }
  put<T = any>(path: string, body: unknown): Promise<T> {
    return this.raw<T>("PUT", path, body);
  }
  del<T = any>(path: string): Promise<T> {
    return this.raw<T>("DELETE", path);
  }

  /** Открыть HTML/файл с авторизацией (накладная, сменное задание, выгрузка Excel). */
  async open(path: string, params?: Record<string, any>, filename?: string): Promise<void> {
    const h: Record<string, string> = {};
    if (this.token) h["Authorization"] = `Bearer ${this.token}`;
    const res = await fetch(this.base + path + qs(params), { headers: h });
    if (!res.ok) throw new ApiError(res.status, "http", `Не удалось открыть файл (${res.status})`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    if (filename) {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
    } else {
      window.open(url, "_blank");
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}

export function qs(params?: Record<string, any>): string {
  if (!params) return "";
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) v.forEach((x) => u.append(k, String(x)));
    else u.append(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

export function uid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}
